import { tokens } from "@/art/tokens";
import type { ItemRarity, OutcomeBand, RarityDisplay } from "@/api/types";

export const STAT_LABEL_KO: Record<string, string> = {
    str: "완력",
    vit: "체력",
    int: "지능",
    spi: "정신",
    def: "방어",
    spd: "속력",
    cha: "매력",
    tec: "기술",
    // 2026-04-23 사용자 피드백: "기프트" 워딩이 어색하다 → "재능" 으로 교체.
    //   "재능" 은 한국어 게이머에게 즉각 읽히는 용어 (패시브 특성 느낌).
    gift: "재능",
    art: "전투 기예",
    destiny: "운명",
};

export const STAT_PRIMARY: string[] = ["str", "vit", "int", "spi", "spd"];
export const STAT_SECONDARY: string[] = ["def", "cha", "tec"];

export function labelForStat(code: string): string {
    return STAT_LABEL_KO[code] ?? code.toUpperCase();
}

export interface RarityPalette {
    border: string;
    text: string;
    glow: string;
    fillSoft: string;
}

export const RARITY_PALETTE: Record<string, RarityPalette> = {
    slate: {
        border: tokens.color.rarity.slate,
        text: "#B4BCC7",
        glow: "rgba(74, 85, 104, 0.25)",
        fillSoft: "rgba(74, 85, 104, 0.15)",
    },
    moss: {
        border: tokens.color.rarity.moss,
        text: "#A3BFA3",
        glow: "rgba(62, 90, 58, 0.35)",
        fillSoft: "rgba(62, 90, 58, 0.18)",
    },
    brass: {
        border: tokens.color.rarity.brass,
        text: "#D9BB8A",
        glow: "rgba(169, 123, 61, 0.40)",
        fillSoft: "rgba(169, 123, 61, 0.20)",
    },
    ember: {
        border: tokens.color.rarity.ember,
        text: "#E8A97D",
        glow: "rgba(201, 106, 59, 0.50)",
        fillSoft: "rgba(201, 106, 59, 0.22)",
    },
    burgundy: {
        border: tokens.color.rarity.burgundy,
        text: "#C89AA3",
        glow: "rgba(92, 31, 46, 0.55)",
        fillSoft: "rgba(92, 31, 46, 0.25)",
    },
};

export function paletteFor(rarity?: RarityDisplay | ItemRarity | null): RarityPalette {
    if (!rarity) return RARITY_PALETTE.slate;
    const token = typeof rarity === "string" ? rarityFallback(rarity) : rarity.paletteToken;
    return RARITY_PALETTE[token] ?? RARITY_PALETTE.slate;
}

function rarityFallback(r: ItemRarity): string {
    return {
        COMMON: "slate",
        UNCOMMON: "moss",
        RARE: "brass",
        EPIC: "ember",
        LEGENDARY: "burgundy",
    }[r] ?? "slate";
}

export interface OutcomePresentation {
    labelKo: string;
    color: string;
    glow: string;
}

export const OUTCOME_PRESENTATION: Record<OutcomeBand, OutcomePresentation> = {
    GREAT_SUCCESS: { labelKo: "대성공", color: tokens.color.outcome.great, glow: "rgba(201,106,59,0.45)" },
    SUCCESS: { labelKo: "성공", color: tokens.color.outcome.success, glow: "rgba(62,90,58,0.35)" },
    POOR: { labelKo: "불안", color: tokens.color.outcome.poor, glow: "rgba(169,123,61,0.35)" },
    FAILURE: { labelKo: "실패", color: tokens.color.outcome.failure, glow: "rgba(92,31,46,0.45)" },
};

export function conditionFor(stamina: number, mana: number): {
    emoji: string;
    labelKo: string;
    tone: "fresh" | "steady" | "tired" | "fatigued" | "injured";
} {
    const m = Math.min(stamina, mana);
    if (m >= 80) return { emoji: "빛", labelKo: "쾌조", tone: "fresh" };
    if (m >= 60) return { emoji: "결", labelKo: "안정", tone: "steady" };
    if (m >= 40) return { emoji: "흔", labelKo: "나른", tone: "tired" };
    if (m >= 20) return { emoji: "균", labelKo: "피로", tone: "fatigued" };
    return { emoji: "흉", labelKo: "위태", tone: "injured" };
}

/** 운명 팩의 `weight` 필드 — 이번 여정에서 이 팩이 얼마나 자주·깊게 개입하느냐를 낮음/보통/높음/격렬 로 표현. */
export const DESTINY_WEIGHT_LABEL_KO: Record<string, string> = {
    low: "가벼움",
    medium: "보통",
    high: "무거움",
    critical: "격렬",
};

export function labelForDestinyWeight(weight: string): string {
    return DESTINY_WEIGHT_LABEL_KO[weight.toLowerCase()] ?? weight;
}

export const ACTION_LABEL_KO: Record<string, string> = {
    TRAINING: "훈련",
    REST: "휴식",
    CRAFTING: "제작",
    OUTING: "외출",
    RESEARCH: "연구",
    SCOUTING: "정찰",
    SCENARIO_EVENT: "이야기",
};

export const ACTION_ICON: Record<string, string> = {
    TRAINING: "⚔",
    REST: "☕",
    CRAFTING: "⚒",
    OUTING: "✦",
    RESEARCH: "⌘",
    SCOUTING: "➤",
    SCENARIO_EVENT: "✶",
};

export const ACTION_ICON_PATH: Record<string, string> = {
    TRAINING: "/art/icons/actions/training-v2.png",
    REST: "/art/icons/actions/rest-v2.png",
    CRAFTING: "/art/icons/actions/crafting-v2.png",
    OUTING: "/art/icons/actions/outing-v2.png",
    RESEARCH: "/art/icons/actions/research-v2.png",
    SCOUTING: "/art/icons/actions/scouting-v2.png",
    SCENARIO_EVENT: "/art/icons/actions/ritual-v2.png",
};

export interface ActionTheme {
    accent: string;
    softFill: string;
    halo: string;
    plate: string;
    glow: string;
}

export const ACTION_THEME: Record<string, ActionTheme> = {
    TRAINING: {
        accent: tokens.color.ember,
        softFill: "rgba(201,106,59,0.16)",
        halo: "radial-gradient(circle at 50% 32%, rgba(201,106,59,0.26) 0%, rgba(201,106,59,0) 68%)",
        plate: "linear-gradient(180deg, rgba(50,26,22,0.92), rgba(20,16,20,0.96))",
        glow: "rgba(201,106,59,0.42)",
    },
    REST: {
        accent: tokens.color.brass,
        softFill: "rgba(169,123,61,0.14)",
        halo: "radial-gradient(circle at 50% 32%, rgba(169,123,61,0.22) 0%, rgba(169,123,61,0) 68%)",
        plate: "linear-gradient(180deg, rgba(42,31,20,0.9), rgba(18,16,18,0.96))",
        glow: "rgba(169,123,61,0.34)",
    },
    CRAFTING: {
        accent: "#C88957",
        softFill: "rgba(200,137,87,0.14)",
        halo: "radial-gradient(circle at 50% 32%, rgba(200,137,87,0.24) 0%, rgba(200,137,87,0) 68%)",
        plate: "linear-gradient(180deg, rgba(46,28,20,0.92), rgba(18,14,18,0.96))",
        glow: "rgba(200,137,87,0.36)",
    },
    OUTING: {
        accent: "#9E7F57",
        softFill: "rgba(158,127,87,0.14)",
        halo: "radial-gradient(circle at 50% 32%, rgba(158,127,87,0.24) 0%, rgba(158,127,87,0) 68%)",
        plate: "linear-gradient(180deg, rgba(42,30,22,0.92), rgba(16,14,18,0.96))",
        glow: "rgba(158,127,87,0.34)",
    },
    RESEARCH: {
        accent: "#8EA8C7",
        softFill: "rgba(142,168,199,0.14)",
        halo: "radial-gradient(circle at 50% 32%, rgba(142,168,199,0.24) 0%, rgba(142,168,199,0) 68%)",
        plate: "linear-gradient(180deg, rgba(24,30,42,0.92), rgba(16,14,18,0.96))",
        glow: "rgba(142,168,199,0.34)",
    },
    SCOUTING: {
        accent: "#8AA37B",
        softFill: "rgba(138,163,123,0.14)",
        halo: "radial-gradient(circle at 50% 32%, rgba(138,163,123,0.24) 0%, rgba(138,163,123,0) 68%)",
        plate: "linear-gradient(180deg, rgba(24,34,24,0.92), rgba(16,14,18,0.96))",
        glow: "rgba(138,163,123,0.34)",
    },
    SCENARIO_EVENT: {
        accent: tokens.color.burgundy,
        softFill: "rgba(92,31,46,0.18)",
        halo: "radial-gradient(circle at 50% 32%, rgba(92,31,46,0.28) 0%, rgba(92,31,46,0) 68%)",
        plate: "linear-gradient(180deg, rgba(38,18,26,0.92), rgba(16,14,18,0.96))",
        glow: "rgba(92,31,46,0.4)",
    },
};

export const STATE_ICON_PATH = {
    vitality: "/art/icons/states/vitality.png",
    spirit: "/art/icons/states/spirit.png",
    condition: "/art/icons/states/condition.png",
    destinyThread: "/art/icons/states/destiny-thread.png",
    taleOmen: "/art/icons/states/tale-omen.png",
    houseChronicle: "/art/icons/states/house-chronicle.png",
} as const;
